// ─── ToolCard — central (agent-agnostic) renderer for a tool_call / tool_result ──────
//
// SHARED rendering (per the split in agents/toolAction.ts): given a NORMALIZED `ToolAction`
// (produced by the per-agent mapper), draw a ONE-LINE summary that folds/unfolds on tap. The
// interpretation "which agent calls its edit tool what" lives in agents/<name>.ts; this file
// never looks at raw tool names — it only knows ToolAction kinds, so EVERY agent reuses it.
//
// Folded = a single compact row (icon · verb · target · stat). Tap/click → expand the rich body:
// Edit→red/green diff · Write→code · Bash→command · search/fetch/task→args · todos→checklist ·
// unknown→pretty JSON. Bodies reuse the existing .code-block / .code-block-diff CSS.

import { useState } from 'react'
import type { ReactNode } from 'react'
import type { ConversationTurn } from '../../api/client'
import { getToolAction, basename, clip, type ToolAction } from '../../agents/toolAction'

const MAX_BODY_ROWS = 600 // cap rendered lines so a huge edit/file can't lock the UI

// ─── diff body ──────────────────────────────────────────────────────────────────────

interface DiffRow { kind: 'add' | 'del' | 'ctx'; text: string }

// Line diff of one replace (old → new): trim the shared head/tail, keep up to `ctx` context
// lines. No absolute file line numbers (not in the transcript) → the gutter stays blank.
function diffRows(oldStr: string, newStr: string, ctx = 3): { rows: DiffRow[]; adds: number; dels: number } {
  const o = oldStr.split('\n')
  const n = newStr.split('\n')
  let start = 0
  while (start < o.length && start < n.length && o[start] === n[start]) start++
  let oe = o.length
  let ne = n.length
  while (oe > start && ne > start && o[oe - 1] === n[ne - 1]) { oe--; ne-- }
  const rows: DiffRow[] = []
  for (const c of o.slice(Math.max(start - ctx, 0), start)) rows.push({ kind: 'ctx', text: c })
  for (const r of o.slice(start, oe)) rows.push({ kind: 'del', text: r })
  for (const a of n.slice(start, ne)) rows.push({ kind: 'add', text: a })
  for (const c of o.slice(oe, Math.min(oe + ctx, o.length))) rows.push({ kind: 'ctx', text: c })
  return { rows, adds: ne - start, dels: oe - start }
}

function DiffBody({ rows }: { rows: DiffRow[] }) {
  const shown = rows.slice(0, MAX_BODY_ROWS)
  const cls = (k: DiffRow['kind']) => (k === 'add' ? 'diff-add' : k === 'del' ? 'diff-del' : 'diff-context')
  const sign = (k: DiffRow['kind']) => (k === 'add' ? '+' : k === 'del' ? '-' : ' ')
  return (
    <pre className="code-block tool-card-pre"><code className="code-block-diff">
      {shown.map((r, i) => (
        <span className={`diff-line ${cls(r.kind)}`} key={i}>
          <span className="diff-gutter" />
          <span className="diff-sign">{sign(r.kind)}</span>
          <span className="diff-text">{r.text || ' '}</span>
        </span>
      ))}
      {rows.length > MAX_BODY_ROWS && (
        <span className="diff-line diff-context"><span className="diff-gutter" /><span className="diff-sign"> </span><span className="diff-text">… อีก {rows.length - MAX_BODY_ROWS} บรรทัด</span></span>
      )}
    </code></pre>
  )
}

function CodeBody({ text }: { text: string }) {
  const lines = text.split('\n')
  const shown = lines.length > MAX_BODY_ROWS ? lines.slice(0, MAX_BODY_ROWS).join('\n') + `\n… อีก ${lines.length - MAX_BODY_ROWS} บรรทัด` : text
  return <pre className="code-block tool-card-pre"><code>{shown}</code></pre>
}

// ─── the collapsible card ─────────────────────────────────────────────────────────────

interface ToolCardProps {
  icon: string
  verb?: string
  detail?: string
  stat?: ReactNode
  body?: ReactNode      // omit → summary only (not expandable)
  defaultOpen?: boolean
}

function ToolCard({ icon, verb, detail, stat, body, defaultOpen = false }: ToolCardProps) {
  const [open, setOpen] = useState(defaultOpen)
  const summary = (
    <>
      <span className="tool-card-icon">{icon}</span>
      {verb && <span className="tool-card-verb">{verb}</span>}
      {detail && <span className="tool-card-detail">{detail}</span>}
      {stat != null && <span className="tool-card-stat">{stat}</span>}
    </>
  )
  if (body == null) return <div className="tool-card tool-card--static">{summary}</div>
  return (
    <div className={`tool-card${open ? ' is-open' : ''}`}>
      <button className="tool-card-head" type="button" onClick={() => setOpen(v => !v)} aria-expanded={open}>
        <span className="tool-card-caret">{open ? '▾' : '▸'}</span>
        {summary}
      </button>
      {open && <div className="tool-card-body">{body}</div>}
    </div>
  )
}

function Stat({ adds, dels }: { adds: number; dels: number }) {
  return (
    <>
      {adds > 0 && <span className="tool-stat-add">+{adds}</span>}
      {dels > 0 && <span className="tool-stat-del">−{dels}</span>}
    </>
  )
}

// ─── ToolAction → card (the ONE place presentation is decided, for ALL agents) ──────────

function renderToolAction(a: ToolAction): ReactNode {
  switch (a.kind) {
    case 'edit': {
      let adds = 0, dels = 0
      const rows: DiffRow[] = []
      a.hunks.forEach((h, i) => {
        if (i > 0) rows.push({ kind: 'ctx', text: '' })
        const d = diffRows(h.old, h.new)
        adds += d.adds; dels += d.dels
        rows.push(...d.rows)
      })
      const multi = a.hunks.length > 1 ? ` · ${a.hunks.length} จุด` : ''
      return <ToolCard icon="✎" verb="Edit" detail={(basename(a.file) || a.file) + multi} stat={<Stat adds={adds} dels={dels} />} body={rows.length ? <DiffBody rows={rows} /> : undefined} />
    }
    case 'write': {
      const n = a.content ? a.content.split('\n').length : 0
      return <ToolCard icon="✚" verb="Write" detail={basename(a.file) || a.file} stat={n ? <span className="tool-stat-add">+{n}</span> : undefined} body={a.content ? <CodeBody text={a.content} /> : undefined} />
    }
    case 'read': {
      const range = a.offset != null ? ` บรรทัด ${a.offset}${a.limit != null ? `–${a.offset + a.limit}` : '+'}` : ''
      return <ToolCard icon="▤" verb="Read" detail={(basename(a.file) || a.file) + range} />
    }
    case 'bash':
      return <ToolCard icon="❯" detail={clip(a.command, 90)} body={(a.command.length > 90 || a.description) ? <CodeBody text={(a.description ? `# ${a.description}\n` : '') + a.command} /> : undefined} />
    case 'search': {
      const verb = a.engine === 'grep' ? 'Grep' : a.engine === 'glob' ? 'Glob' : 'LS'
      const icon = a.engine === 'ls' ? '▤' : '⌕'
      const detail = a.engine === 'ls' ? (basename(a.query) || a.query) : `${clip(a.query, 50)}${a.path ? ' · ' + basename(a.path) : ''}`
      return <ToolCard icon={icon} verb={verb} detail={detail} />
    }
    case 'fetch': {
      let host = a.url
      try { host = new URL(a.url).host } catch { /* keep raw */ }
      return <ToolCard icon="🌐" verb="Fetch" detail={host} body={<CodeBody text={a.url} />} />
    }
    case 'websearch':
      return <ToolCard icon="🔎" verb="Search" detail={`"${clip(a.query, 60)}"`} />
    case 'todos': {
      const done = a.todos.filter(t => t.status === 'completed').length
      const body = a.todos.length ? (
        <ul className="tool-todos">
          {a.todos.map((t, i) => (
            <li key={i} className={`tool-todo tool-todo--${t.status}`}>
              <span className="tool-todo-box">{t.status === 'completed' ? '☑' : t.status === 'in_progress' ? '◐' : '☐'}</span>
              <span className="tool-todo-text">{t.content}</span>
            </li>
          ))}
        </ul>
      ) : undefined
      return <ToolCard icon="☑" verb="Todos" detail={`${done}/${a.todos.length}`} body={body} />
    }
    case 'task':
      return <ToolCard icon="⚙" verb="Task" detail={`${a.subagent ? a.subagent + ': ' : ''}${clip(a.description, 60)}`} body={a.description ? <CodeBody text={a.description} /> : undefined} />
    case 'generic':
      return <ToolCard icon="⚡" verb={a.name} body={a.raw ? <CodeBody text={a.raw} /> : undefined} />
  }
}

/** Render a tool_call as a compact card, using the given agent's mapper. null = nothing to show
 *  (caller falls back to its generic renderer). */
export function renderToolCard(turn: ConversationTurn, agent: string | null | undefined): ReactNode | null {
  const action = getToolAction(agent, turn)
  return action ? renderToolAction(action) : null
}

// ─── tool_result → compact card (agent-agnostic — output is just text) ──────────────────

/** A tool_result folded to one line, expandable to the full monospace output (preserves Read
 *  line numbers / Bash output alignment). */
export function renderToolResult(turn: ConversationTurn): ReactNode {
  const content = turn.content ?? ''
  const lines = content ? content.split('\n').length : 0
  return (
    <ToolCard
      icon="⤷"
      detail={clip(content, 70) || 'ผลลัพธ์'}
      stat={lines > 1 ? <span className="tool-card-dim">{lines} บรรทัด</span> : undefined}
      body={content ? <CodeBody text={content} /> : undefined}
    />
  )
}
